import { getCurrentLocale } from "./i18n/core";

function getServerOrigin(): string {
  if (process.env.BACKEND_URL) {
    try {
      return new URL(process.env.BACKEND_URL).origin;
    } catch {
      // Ignore invalid backend URL and keep trying fallbacks.
    }
  }

  const appUrl =
    process.env.NEXT_PUBLIC_APP_URL ||
    process.env.APP_URL ||
    process.env.NEXTAUTH_URL;
  if (appUrl) {
    try {
      return new URL(appUrl).origin;
    } catch {
      // Ignore invalid URL and keep trying fallbacks.
    }
  }

  if (process.env.NEXT_PUBLIC_API_URL) {
    try {
      return new URL(process.env.NEXT_PUBLIC_API_URL).origin;
    } catch {
      // Ignore invalid URL and keep trying fallbacks.
    }
  }

  if (process.env.VERCEL_URL) {
    return `https://${process.env.VERCEL_URL}`;
  }

  const port = process.env.PORT || "3000";
  return `http://127.0.0.1:${port}`;
}

async function getServerCookieToken(): Promise<string | null> {
  if (typeof window !== "undefined") {
    return null;
  }

  try {
    const { cookies } = await import("next/headers");
    const store = await cookies();
    return store.get("alita_token")?.value || null;
  } catch {
    return null;
  }
}

function normalizeApiUrl(input: string): string {
  const value = (input || "").trim();
  if (value === "") {
    return value;
  }

  const isBrowser = typeof window !== "undefined";
  if (!isBrowser) {
    if (value === "undefined" || value === "null") {
      return `${getServerOrigin()}/api`;
    }

    if (value.startsWith("undefined/") || value.startsWith("null/")) {
      const cleanPath = value.replace(/^(undefined|null)/, "");
      const path = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
      return `${getServerOrigin()}/api${path}`;
    }

    try {
      return new URL(value).toString();
    } catch {
      const path = value.startsWith("/") ? value : `/${value}`;
      const apiPath = path.startsWith("/api") ? path : `/api${path}`;
      return `${getServerOrigin()}${apiPath}`;
    }
  }

  if (value === "undefined" || value === "null") {
    return `${window.location.origin}/api`;
  }

  if (value.startsWith("undefined/") || value.startsWith("null/")) {
    const cleanPath = value.replace(/^(undefined|null)/, "");
    const path = cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
    return `${window.location.origin}/api${path}`;
  }

  if (value.startsWith("/")) {
    const path = value.startsWith("/api") ? value : `/api${value}`;
    return `${window.location.origin}${path}`;
  }

  try {
    const parsed = new URL(value);
    const sameHostAsBrowser = parsed.hostname === window.location.hostname;

    // If API URL points to the same host (e.g. :8000) normalize it to same-origin
    // so requests go through Next.js /api rewrite and avoid browser CORS preflight issues.
    if (sameHostAsBrowser) {
      const path = parsed.pathname.startsWith("/api")
        ? parsed.pathname
        : `/api${parsed.pathname}`;
      return `${window.location.origin}${path}${parsed.search}${parsed.hash}`;
    }

    const isLocalhost =
      parsed.hostname === "localhost" ||
      parsed.hostname === "127.0.0.1" ||
      parsed.hostname === "0.0.0.0";

    if (!isLocalhost) {
      return value;
    }

    const needsApiPrefix = !parsed.pathname.startsWith("/api");
    const apiPrefix = needsApiPrefix ? "/api" : "";
    return `${window.location.origin}${apiPrefix}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return value;
  }
}

const AUTH_STORAGE_KEYS = [
  "token",
  "user_id",
  "user_name",
  "user_role",
  "user_email",
  "user_job_title",
  "rbac_roles",
  "rbac_permissions",
  "rbac_permission_scopes",
  "user_allowed_modules",
  "user_data",
  "lastActivity",
  "logoutAt",
  "sso_logout_url",
];

const AUTH_COOKIE_KEYS = [
  "alita_token",
  "user_id",
  "user_name",
  "user_role",
  "user_email",
  "rbac_roles",
  "rbac_permissions",
  "rbac_permission_scopes",
  "user_allowed_modules",
  "portal_guest",
];

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  const fromStorage = localStorage.getItem("token") || localStorage.getItem("alita_token");
  if (fromStorage) return fromStorage;

  if (typeof document !== "undefined") {
    const match = document.cookie.split("; ").find((row) => row.startsWith("alita_token=") || row.startsWith("token="));
    if (match) return match.split("=")[1] || null;
  }
  return null;
}

export function clearAuthStorage() {
  if (typeof window !== "undefined") {
    AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  }

  if (typeof document !== "undefined") {
    AUTH_COOKIE_KEYS.forEach((key) => {
      document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    });
  }
}

/**
 * Opens the portal for someone who signed in with Alita SSO but has no AiBAS
 * account. There is no API token: the cookie only lets the middleware show the
 * module portal, and each module still signs the user in against its own users.
 */
export function startPortalGuestSession(
  user: { name?: string | null; email?: string | null } | null | undefined,
  ssoLogoutUrl: unknown,
) {
  clearAuthStorage();

  const secure = window.location.protocol === "https:" ? "; secure" : "";
  document.cookie = `portal_guest=1; path=/; max-age=86400; samesite=strict${secure}`;

  localStorage.setItem("user_name", user?.name || user?.email || "");
  localStorage.setItem("user_email", user?.email || "");
  localStorage.setItem("user_allowed_modules", JSON.stringify(["atp"]));
  localStorage.setItem("lastActivity", Date.now().toString());
  if (typeof ssoLogoutUrl === "string" && ssoLogoutUrl) {
    localStorage.setItem("sso_logout_url", ssoLogoutUrl);
  }
}

/**
 * Ends the AiBAS session. When it was opened through Alita SSO, the other portal
 * modules are signed out too and the Keycloak logout URL is returned: the caller
 * must send the browser there to end the SSO session. Returns null otherwise.
 */
export async function endAuthSession(): Promise<string | null> {
  const ssoLogoutUrl =
    typeof window !== "undefined" ? localStorage.getItem("sso_logout_url") : null;

  // A portal guest has no AiBAS token to revoke.
  if (getAuthToken()) {
    try {
      await fetchWithAuth(`${process.env.NEXT_PUBLIC_API_URL || "/api"}/logout`, {
        method: "POST",
      });
    } catch {
      // Ignore logout network errors and proceed with local cleanup.
    }
  }

  if (ssoLogoutUrl) {
    try {
      await fetch("/atpro/api/auth/logout", { method: "POST" });
    } catch {
      // ATPRO being unreachable must not block the portal logout.
    }
  }

  clearAuthStorage();
  return ssoLogoutUrl;
}

export async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const apiUrl = normalizeApiUrl(url);
  const isBrowser = typeof window !== "undefined";
  const browserToken = isBrowser ? getAuthToken() : null;
  const serverCookieToken = !isBrowser ? await getServerCookieToken() : null;
  // SERVER_API_TOKEN remains a fallback for background/server-only rendering
  // where there is no user request cookie.
  const serverToken = !isBrowser
    ? serverCookieToken || process.env.SERVER_API_TOKEN || null
    : null;
  const token = browserToken || serverToken;

  // Only set Content-Type if body is not FormData
  const headers: Record<string, string> = {};
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  // Merge with provided headers
  if (options.headers && typeof options.headers === "object") {
    Object.assign(headers, options.headers);
  }

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (!headers["Accept-Language"]) {
    headers["Accept-Language"] = getCurrentLocale();
  }

  const response = await fetch(apiUrl, {
    ...options,
    headers,
  });

  // Handle 401 carefully: only auto-logout when token/auth is truly invalid.
  // Some backend middlewares may return 401 for role/permission denial.
  if (response.status === 401) {
    let message = "";
    try {
      const cloned = response.clone();
      const payload = await cloned.json();
      message = String(payload?.message || "");
    } catch {
      message = "";
    }

    const authFailureMessages = [
      "Unauthenticated",
      "Authentication required",
      "Invalid token",
      "Token has expired",
    ];

    const shouldLogout =
      !token || authFailureMessages.some((item) => message.includes(item));

    if (shouldLogout && isBrowser) {
      clearAuthStorage();
      window.location.href = "/login";
    }
  }

  return response;
}

export { normalizeApiUrl };

// Download file with authentication
export async function downloadFile(url: string, filename: string) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    throw new Error("downloadFile can only run in the browser");
  }

  try {
    const response = await fetchWithAuth(url);
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }
    const contentType =
      response.headers.get("content-type") || "application/octet-stream";
    const buffer = await response.arrayBuffer();
    const blob = new Blob([buffer], { type: contentType });

    // Edge/IE fallback
    const navigatorWithMsSave =
      typeof window !== "undefined"
        ? (window.navigator as Navigator & {
            msSaveOrOpenBlob?: (blobPart: Blob, defaultName?: string) => void;
          })
        : null;
    if (
      navigatorWithMsSave &&
      typeof navigatorWithMsSave.msSaveOrOpenBlob === "function"
    ) {
      navigatorWithMsSave.msSaveOrOpenBlob(blob, filename);
      return;
    }

    const link = document.createElement("a");
    link.download = filename;
    link.style.display = "none";

    const blobUrl = URL.createObjectURL(blob);
    link.href = blobUrl;
    document.body.appendChild(link);
    link.click();
    // Cleanup after click to avoid stalled download state in some browsers
    setTimeout(() => {
      URL.revokeObjectURL(blobUrl);
      document.body.removeChild(link);
    }, 1000);
  } catch (error) {
    console.error("Download error:", error);
    throw error;
  }
}
